/**
 * InterLogs Trade — 24-Hour Market Pulse Grid & Spline Engine (TypeScript)
 *
 * Implements continuous Catmull-Rom splines across all 24 hours, area fill gradients,
 * animated radar dot for the live candle, real-time micro-ticks, and price flashing.
 */

import { MarketSnapshot, MatrixRow, HourBucket } from './types';

export interface TickerConfigItem {
  section?: string;
  ticker?: string;
  type?: 'index' | 'crypto';
}

export type SortMode = 'growth' | 'fall' | 'market_cap' | 'name';

export const TICKER_CONFIG: TickerConfigItem[] = [

  // --- Indices ---
  { section: 'MARKET INDICES' },
  { ticker: 'S&P 500', type: 'index' },
  { ticker: 'NASDAQ', type: 'index' },
  { ticker: 'DOW JONES', type: 'index' },
  { ticker: 'FTSE 100', type: 'index' },
  { ticker: 'DAX 40', type: 'index' },
  { ticker: 'CAC 40', type: 'index' },
  { ticker: 'NIKKEI 225', type: 'index' },
  { ticker: 'HANG SENG', type: 'index' },
  { ticker: 'SHANGHAI', type: 'index' },
  { ticker: 'STOXX 50', type: 'index' },
  // --- Commodities ---
  { section: 'COMMODITIES' },
  { ticker: 'BRENT OIL', type: 'index' },
  { ticker: 'GOLD', type: 'index' },
  // --- Crypto (Top 100 Spot) ---
  { section: 'CRYPTO' },
  { ticker: 'BTC', type: 'crypto' },
  { ticker: 'ETH', type: 'crypto' },
  { ticker: 'SOL', type: 'crypto' },
  { ticker: 'BNB', type: 'crypto' },
  { ticker: 'XRP', type: 'crypto' },
  { ticker: 'DOGE', type: 'crypto' },
  { ticker: 'ADA', type: 'crypto' },
  { ticker: 'AVAX', type: 'crypto' },
  { ticker: 'LINK', type: 'crypto' },
  { ticker: 'SUI', type: 'crypto' },
  { ticker: 'NEAR', type: 'crypto' },
  { ticker: 'TON', type: 'crypto' },
  { ticker: 'DOT', type: 'crypto' },
  { ticker: 'SHIB', type: 'crypto' },
  { ticker: 'LTC', type: 'crypto' },
  { ticker: 'BCH', type: 'crypto' },
  { ticker: 'UNI', type: 'crypto' },
  { ticker: 'TRX', type: 'crypto' },
  { ticker: 'XLM', type: 'crypto' },
  { ticker: 'APT', type: 'crypto' },
  { ticker: 'PEPE', type: 'crypto' },
  { ticker: 'TAO', type: 'crypto' },
  { ticker: 'AAVE', type: 'crypto' },
  { ticker: 'FIL', type: 'crypto' },
  { ticker: 'ARB', type: 'crypto' },
  { ticker: 'OP', type: 'crypto' },
  { ticker: 'INJ', type: 'crypto' },
  { ticker: 'ATOM', type: 'crypto' },
  { ticker: 'TIA', type: 'crypto' },
  { ticker: 'SEI', type: 'crypto' },
  { ticker: 'POL', type: 'crypto' },
  { ticker: 'VET', type: 'crypto' },
  { ticker: 'HBAR', type: 'crypto' },
  { ticker: 'ETC', type: 'crypto' },
  { ticker: 'ALGO', type: 'crypto' },
  { ticker: 'RUNE', type: 'crypto' },
  { ticker: 'STX', type: 'crypto' },
  { ticker: 'S', type: 'crypto' },
  { ticker: 'JUP', type: 'crypto' },
  { ticker: 'WIF', type: 'crypto' },
  { ticker: 'FLOKI', type: 'crypto' },
  { ticker: 'BONK', type: 'crypto' },
  { ticker: 'ENA', type: 'crypto' },
  { ticker: 'FET', type: 'crypto' },
  { ticker: 'RENDER', type: 'crypto' },
  { ticker: 'LDO', type: 'crypto' },
  { ticker: 'ICP', type: 'crypto' },
  { ticker: 'AR', type: 'crypto' },
  { ticker: 'ONDO', type: 'crypto' },
  { ticker: 'WLD', type: 'crypto' },
  // Verified Liquid Spot Crypto
  { ticker: 'PENDLE', type: 'crypto' },
  { ticker: 'CRV', type: 'crypto' },
  { ticker: '1INCH', type: 'crypto' },
  { ticker: 'CAKE', type: 'crypto' },
  { ticker: 'COMP', type: 'crypto' },
  { ticker: 'SNX', type: 'crypto' },
  { ticker: 'DYDX', type: 'crypto' },
  { ticker: 'PYTH', type: 'crypto' },
  { ticker: 'JTO', type: 'crypto' },
  { ticker: 'RAY', type: 'crypto' },
  { ticker: 'ORDI', type: 'crypto' },
  { ticker: 'ENS', type: 'crypto' },
  { ticker: 'SAGA', type: 'crypto' },
  { ticker: 'STRK', type: 'crypto' },
  { ticker: 'ZRO', type: 'crypto' },
  { ticker: 'ROSE', type: 'crypto' },
  { ticker: 'CFX', type: 'crypto' },
  { ticker: 'GALA', type: 'crypto' },
  { ticker: 'SAND', type: 'crypto' },
  { ticker: 'MANA', type: 'crypto' },
  { ticker: 'AXS', type: 'crypto' },
  { ticker: 'BLUR', type: 'crypto' },
  { ticker: 'CHZ', type: 'crypto' },
  { ticker: 'APE', type: 'crypto' },
  { ticker: 'NOT', type: 'crypto' },
  { ticker: 'MANTA', type: 'crypto' },
  { ticker: 'EGLD', type: 'crypto' },
];

export interface LivePriceInfo {
  price: number;
  lastPrice: number;
  changePct: number;
  isUp: boolean;
  lastUpdated: number;
}

export class MarketGridController {
  private gridContainer: HTMLElement;
  private hoursRow: HTMLElement;
  private tooltipEl: HTMLElement | null = null;
  private clockEl: HTMLElement | null = null;
  private statusDot: HTMLElement | null = null;
  private statusText: HTMLElement | null = null;

  private matrixData: Map<string, MatrixRow> = new Map();
  private livePrices: Map<string, LivePriceInfo> = new Map();
  private cellCanvases: Map<string, HTMLCanvasElement[]> = new Map();

  private previousRanks: Map<string, number> = new Map();
  private sectionDividers: Map<string, HTMLElement> = new Map();
  private sectionGroups: Map<string, string[]> = new Map();
  private rankingEnabled: boolean = true;
  private currentSortMode: SortMode = 'growth';
  private canonicalCapIndex: Map<string, number> = new Map();

  private lastDrawnHour: number = -1;
  private pulsePhase: number = 0;
  private lastFrameTime: number = 0;

  constructor() {
    this.gridContainer = document.getElementById('grid-container')!;
    this.hoursRow = document.getElementById('hours-row')!;
    this.tooltipEl = document.getElementById('trade-tooltip');
    this.clockEl = document.getElementById('clock');
    this.statusDot = document.getElementById('status-dot');
    this.statusText = document.getElementById('status-text');

    // Index canonical cap order
    let capRank = 1;
    for (const item of TICKER_CONFIG) {
      if (item.ticker) {
        this.canonicalCapIndex.set(item.ticker, capRank++);
      }
    }

    try {
      const savedSort = localStorage.getItem('interlogs_trade_sort') as SortMode | null;
      if (savedSort && ['growth', 'fall', 'market_cap', 'name'].includes(savedSort)) {
        this.currentSortMode = savedSort;
      }
    } catch (e) {}

    this.renderHourHeaders();
    this.buildGrid();
    this.startClock();
    this.setupTooltipEvents();
    this.setupResponsiveResize();
    this.startOptimizedRenderLoop();
    this.startMicroTickEngine();
  }

  private getLocalTzOffsetHours(): number {
    return -Math.round(new Date().getTimezoneOffset() / 60);
  }

  private renderHourHeaders(): void {
    if (!this.hoursRow) return;
    const now = new Date();
    const currentLocalHour = now.getHours();
    let html = '';
    for (let h = 0; h < 24; h++) {
      const isCurrent = h === currentLocalHour;
      const hourStr = String(h).padStart(2, '0');
      html += `<div class="hour-label ${isCurrent ? 'current-hour' : ''}" id="hour-header-${h}">${hourStr}</div>`;
    }
    this.hoursRow.innerHTML = html;
  }

  private updateCurrentHourHighlight(currentLocalHour: number): void {
    for (let h = 0; h < 24; h++) {
      const el = document.getElementById(`hour-header-${h}`);
      if (el) {
        el.classList.toggle('current-hour', h === currentLocalHour);
      }
    }

    for (const canvases of this.cellCanvases.values()) {
      for (let h = 0; h < 24; h++) {
        const cell = canvases[h]?.parentElement;
        if (!cell) continue;
        const isCurrent = h === currentLocalHour;
        const isPast = h < currentLocalHour;
        const isFuture = h > currentLocalHour;

        cell.classList.toggle('current', isCurrent);
        cell.classList.toggle('past', isPast);
        cell.classList.toggle('future', isFuture);
      }
    }
  }

  private startClock(): void {
    const update = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      let tzInfo = '';
      try {
        const offset = -now.getTimezoneOffset() / 60;
        const sign = offset >= 0 ? '+' : '';
        tzInfo = ` (UTC${sign}${offset})`;
      } catch (e) {}

      if (this.clockEl) {
        this.clockEl.textContent = `${timeStr}${tzInfo}`;
      }

      const currentLocalHour = now.getHours();
      if (currentLocalHour !== this.lastDrawnHour && this.lastDrawnHour !== -1) {
        this.updateCurrentHourHighlight(currentLocalHour);
        this.renderAllStaticCanvases();
      }
      this.lastDrawnHour = currentLocalHour;
      this.updateCurrentHourHighlight(currentLocalHour);
    };
    update();
    setInterval(update, 1000);
  }

  public setConnectionStatus(online: boolean): void {
    if (this.statusDot && this.statusText) {
      if (online) {
        this.statusDot.className = 'status-dot online';
        this.statusText.textContent = 'LIVE';
      } else {
        this.statusDot.className = 'status-dot offline';
        this.statusText.textContent = 'RECONNECTING';
      }
    }
  }

  private buildGrid(): void {
    if (!this.gridContainer) return;
    this.gridContainer.innerHTML = '';
    this.cellCanvases.clear();
    this.sectionDividers.clear();
    this.sectionGroups.clear();

    const now = new Date();
    const currentLocalHour = now.getHours();

    let currentSection = 'MARKET INDICES';
    this.sectionGroups.set(currentSection, []);

    for (const item of TICKER_CONFIG) {
      if (item.section) {
        currentSection = item.section;
        if (!this.sectionGroups.has(currentSection)) {
          this.sectionGroups.set(currentSection, []);
        }

        const divider = document.createElement('div');
        divider.className = 'section-divider';
        divider.id = `divider-${currentSection.toLowerCase().replace(/\s+/g, '-')}`;
        divider.innerHTML = `
          <span class="divider-line"></span>
          <span class="divider-label">${item.section}</span>
          ${item.section === 'CRYPTO' ? '<span class="divider-ranking-badge">SORT: TOP GAINERS</span>' : ''}
          <span class="divider-line"></span>
        `;
        this.gridContainer.appendChild(divider);
        this.sectionDividers.set(currentSection, divider);
        continue;
      }

      if (!item.ticker) continue;
      const ticker = item.ticker;
      this.sectionGroups.get(currentSection)!.push(ticker);

      const row = document.createElement('div');
      row.className = 'asset-row';
      row.id = `row-${ticker}`;

      const label = document.createElement('div');
      label.className = 'asset-label';
      label.innerHTML = `
        <div class="asset-title-row">
          <span class="asset-rank-badge rank-other" id="rank-${ticker}">—</span>
          <span class="asset-ticker" title="${ticker}">${ticker}</span>
          <span class="rank-shift neutral" id="shift-${ticker}">—</span>
          <span class="squares-momentum-badge sq-0" id="sqtag-${ticker}"></span>
        </div>
        <div class="asset-price-row">
          <span class="asset-price" id="price-${ticker}">—</span>
          <span class="asset-change" id="change-${ticker}">—</span>
        </div>
      `;
      row.appendChild(label);

      const strip = document.createElement('div');
      strip.className = 'cell-strip';

      const canvases: HTMLCanvasElement[] = [];

      for (let h = 0; h < 24; h++) {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.id = `cell-${ticker}-${h}`;
        cell.dataset.ticker = ticker;
        cell.dataset.hour = String(h);

        if (h === currentLocalHour) cell.classList.add('current');
        else if (h > currentLocalHour) cell.classList.add('future');
        else cell.classList.add('past');

        const canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';

        cell.appendChild(canvas);
        strip.appendChild(cell);
        canvases.push(canvas);
      }

      this.cellCanvases.set(ticker, canvases);
      row.appendChild(strip);
      this.gridContainer.appendChild(row);
    }

    this.syncCanvasDimensions();
  }

  public syncCanvasDimensions(): void {
    const dpr = window.devicePixelRatio || 1;
    for (const canvases of this.cellCanvases.values()) {
      for (const canvas of canvases) {
        const rect = canvas.getBoundingClientRect();
        const pxW = Math.max(20, Math.floor((rect.width || 52) * dpr));
        const pxH = Math.max(20, Math.floor((rect.height || 52) * dpr));
        if (canvas.width !== pxW || canvas.height !== pxH) {
          canvas.width = pxW;
          canvas.height = pxH;
        }
      }
    }
  }

  private setupResponsiveResize(): void {
    let resizeTimer: number | null = null;
    window.addEventListener('resize', () => {
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        this.syncCanvasDimensions();
        this.renderAllStaticCanvases();
      }, 100);
    });
  }

  public renderMatrix(matrixInput: Record<string, MatrixRow> | MatrixRow[]): void {
    if (!matrixInput) return;

    if (Array.isArray(matrixInput)) {
      for (const row of matrixInput) {
        this.matrixData.set(row.symbol, row);
      }
    } else {
      for (const [sym, row] of Object.entries(matrixInput)) {
        this.matrixData.set(sym, row);
      }
    }

    this.syncCanvasDimensions();
    this.renderAllStaticCanvases();
    this.applyRealTimeRanking();
  }

  public updateSnapshot(snap: MarketSnapshot): void {
    if (!snap) return;

    if (snap.matrix) {
      this.renderMatrix(snap.matrix);
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const minuteProgress = (currentMinute * 60 + currentSecond) / 3600;

    if (snap.indices) {
      for (const idx of snap.indices) {
        this.updatePriceDisplay(idx.symbol, idx.price, idx.change_pct, idx.is_up);
        this.injectLiveTick(idx.symbol, idx.price, currentHour, minuteProgress);
      }
    }

    if (snap.crypto) {
      for (const c of snap.crypto) {
        this.updatePriceDisplay(c.symbol, c.price, c.change_pct_24h, c.is_up);
        this.injectLiveTick(c.symbol, c.price, currentHour, minuteProgress);
      }
    }

    this.applyRealTimeRanking();
  }

  public updatePriceDisplay(symbol: string, price: number, changePct: number, isUp: boolean): void {
    const priceEl = document.getElementById(`price-${symbol}`);
    const changeEl = document.getElementById(`change-${symbol}`);

    const prev = this.livePrices.get(symbol);
    const lastPrice = prev ? prev.price : price;

    this.livePrices.set(symbol, {
      price,
      lastPrice,
      changePct,
      isUp,
      lastUpdated: Date.now(),
    });

    if (priceEl) {
      const formatted = price >= 1000
        ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : price >= 1.0
        ? price.toFixed(2)
        : price.toFixed(6);

      priceEl.textContent = `$${formatted}`;

      if (prev && Math.abs(price - lastPrice) > 0.000001) {
        const flashClass = price > lastPrice ? 'flash-up' : 'flash-down';
        priceEl.classList.remove('flash-up', 'flash-down');
        void priceEl.offsetWidth; // trigger reflow
        priceEl.classList.add(flashClass);
        setTimeout(() => {
          priceEl.classList.remove(flashClass);
        }, 400);
      }
    }

    if (changeEl) {
      const sign = changePct >= 0 ? '+' : '';
      const isSideways = Math.abs(changePct) <= 0.20;
      const arrow = isSideways ? '▶' : (isUp ? '▲' : '▼');
      changeEl.textContent = `${arrow} ${sign}${changePct.toFixed(2)}%`;
      changeEl.className = `asset-change ${isSideways ? 'sideways' : (isUp ? 'up' : 'down')}`;
    }

    const row = document.getElementById(`row-${symbol}`);
    if (row) {
      row.classList.toggle('trend-sideways', Math.abs(changePct) <= 0.20);
    }
  }

  public injectLiveTick(symbol: string, price: number, currentHour: number, minuteProgress: number): void {
    const tMatrix = this.matrixData.get(symbol);
    if (!tMatrix || !tMatrix.hours || !tMatrix.hours[currentHour]) return;

    const bucket = tMatrix.hours[currentHour];
    bucket.close = price;
    if (price > bucket.high) bucket.high = price;
    if (price < bucket.low) bucket.low = price;
    bucket.change_pct = bucket.open > 0 ? ((price - bucket.open) / bucket.open) * 100 : 0;

    if (!bucket.points || bucket.points.length === 0) {
      bucket.points = [price];
    } else {
      const slot = Math.min(11, Math.floor(minuteProgress * 12));
      while (bucket.points.length <= slot) {
        bucket.points.push(price);
      }
      bucket.points[slot] = price;
    }

    tMatrix.current_price = price;
    tMatrix.minute_progress = minuteProgress;
  }

  private startMicroTickEngine(): void {
    setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentSecond = now.getSeconds();
      const minuteProgress = (currentMinute * 60 + currentSecond) / 3600;

      for (const [symbol, info] of this.livePrices.entries()) {
        const volatility = symbol.includes('BTC') || symbol.includes('ETH') || symbol.includes('SOL')
          ? 0.00012
          : 0.00004;
        const delta = (Math.random() - 0.495) * volatility * info.price;
        const microPrice = info.price + delta;

        this.injectLiveTick(symbol, microPrice, currentHour, minuteProgress);
      }

      this.applyRealTimeRanking();
    }, 1000);
  }

  private startOptimizedRenderLoop(): void {
    const loop = (timestamp: number) => {
      if (timestamp - this.lastFrameTime >= 32) {
        this.lastFrameTime = timestamp;
        this.pulsePhase = (this.pulsePhase + 0.06) % (Math.PI * 2);
        this.renderLiveCellsOnly();
      }
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  public isBucketSideways(bucket: HourBucket | null | undefined): boolean {
    if (!bucket) return true;
    const chg = bucket.change_pct ?? (bucket.open > 0 ? ((bucket.close - bucket.open) / bucket.open) * 100 : 0);
    const rangePct = bucket.open > 0 ? ((bucket.high - bucket.low) / bucket.open) * 100 : 0;
    // An hour is sideways (gray) only when movement is genuinely flat:
    // negligible price change (< 0.05%) AND low intra-hour amplitude (< 0.15%)
    return (Math.abs(chg) < 0.05 && rangePct < 0.15) || (bucket.close === bucket.open && rangePct < 0.08);
  }

  public renderAllStaticCanvases(): void {
    const now = new Date();
    const currentLocalHour = now.getHours();

    for (const item of TICKER_CONFIG) {
      if (item.section || !item.ticker) continue;

      const canvases = this.cellCanvases.get(item.ticker);
      const tMatrix = this.matrixData.get(item.ticker);
      if (!canvases || !tMatrix) continue;

      const currentPrice = tMatrix.current_price || 100;
      const minVolatility = currentPrice * 0.008;
      const high = tMatrix.high_24h ?? currentPrice * 1.02;
      const low = tMatrix.low_24h ?? currentPrice * 0.98;
      const rawRange = high - low;
      const range = Math.max(rawRange, minVolatility);
      const padding = range * 0.12;
      const yMin = low - padding;
      const yMax = high + padding;

      for (let h = 0; h < 24; h++) {
        if (h === currentLocalHour) continue;

        const canvas = canvases[h];
        const cell = canvas?.parentElement;
        const bucket = tMatrix.hours ? tMatrix.hours[h] : null;
        if (!canvas) continue;

        const isPast = h < currentLocalHour;
        if (cell && bucket && isPast) {
          const isSideways = this.isBucketSideways(bucket);
          const isBullish = !isSideways && bucket.close >= bucket.open;
          const isBearish = !isSideways && bucket.close < bucket.open;

          cell.classList.toggle('sideways', isSideways);
          cell.classList.toggle('bullish', isBullish);
          cell.classList.toggle('bearish', isBearish);
        }
        this.renderStaticCell(canvas, bucket, isPast, yMin, yMax);
      }
    }

    this.renderLiveCellsOnly();
  }

  private renderLiveCellsOnly(): void {
    const now = new Date();
    const currentLocalHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const progressInHour = (currentMinute * 60 + currentSecond) / 3600;

    for (const item of TICKER_CONFIG) {
      if (item.section || !item.ticker) continue;

      const canvases = this.cellCanvases.get(item.ticker);
      const tMatrix = this.matrixData.get(item.ticker);
      if (!canvases || !tMatrix) continue;

      const canvas = canvases[currentLocalHour];
      const cell = canvas?.parentElement;
      const bucket = tMatrix.hours ? tMatrix.hours[currentLocalHour] : null;
      if (!canvas || !bucket) continue;

      if (cell) {
        const isSideways = this.isBucketSideways(bucket);
        const isBullish = !isSideways && bucket.close >= bucket.open;
        const isBearish = !isSideways && bucket.close < bucket.open;

        cell.classList.toggle('sideways', isSideways);
        cell.classList.toggle('bullish', isBullish);
        cell.classList.toggle('bearish', isBearish);
      }

      const currentPrice = tMatrix.current_price || 100;
      const minVolatility = currentPrice * 0.008;
      const high = tMatrix.high_24h ?? currentPrice * 1.02;
      const low = tMatrix.low_24h ?? currentPrice * 0.98;
      const rawRange = high - low;
      const range = Math.max(rawRange, minVolatility);
      const padding = range * 0.12;
      const yMin = low - padding;
      const yMax = high + padding;

      this.renderActiveHourCell(canvas, bucket, progressInHour, yMin, yMax, this.pulsePhase);
    }
  }

  private renderStaticCell(
    canvas: HTMLCanvasElement,
    bucket: HourBucket | null | undefined,
    isPast: boolean,
    yMin: number,
    yMax: number
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const dpr = window.devicePixelRatio || 1;

    ctx.clearRect(0, 0, w, h);
    if (!bucket || !bucket.points || bucket.points.length === 0) return;

    const points = bucket.points;
    const count = points.length;
    const screenPoints: { x: number; y: number }[] = [];

    for (let i = 0; i < count; i++) {
      const x = count > 1 ? (i / (count - 1)) * w : w / 2;
      const clampedPrice = Math.max(yMin, Math.min(yMax, points[i]));
      const y = h - ((clampedPrice - yMin) / (yMax - yMin)) * (h - 8 * dpr) - 4 * dpr;
      screenPoints.push({ x, y });
    }

    if (screenPoints.length < 2) return;

    if (isPast) {
      const isSideways = this.isBucketSideways(bucket);
      const isBullish = !isSideways && bucket.close >= bucket.open;

      let strokeColor = '#94A3B8';
      let glowColor = 'rgba(148, 163, 184, 0.25)';
      if (!isSideways) {
        strokeColor = isBullish ? '#3CE88E' : '#FF2E56';
        glowColor = isBullish ? 'rgba(60, 232, 142, 0.25)' : 'rgba(255, 46, 86, 0.25)';
      }

      const fillGradient = ctx.createLinearGradient(0, 0, 0, h);
      if (isSideways) {
        fillGradient.addColorStop(0, 'rgba(148, 163, 184, 0.16)');
        fillGradient.addColorStop(0.7, 'rgba(148, 163, 184, 0.03)');
        fillGradient.addColorStop(1, 'rgba(148, 163, 184, 0.0)');
      } else if (isBullish) {
        fillGradient.addColorStop(0, 'rgba(60, 232, 142, 0.20)');
        fillGradient.addColorStop(0.7, 'rgba(60, 232, 142, 0.04)');
        fillGradient.addColorStop(1, 'rgba(60, 232, 142, 0.0)');
      } else {
        fillGradient.addColorStop(0, 'rgba(255, 46, 86, 0.20)');
        fillGradient.addColorStop(0.7, 'rgba(255, 46, 86, 0.04)');
        fillGradient.addColorStop(1, 'rgba(255, 46, 86, 0.0)');
      }

      ctx.fillStyle = fillGradient;
      this.drawCatmullRomSpline(ctx, screenPoints, true, w, h);
      ctx.fill();

      ctx.save();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1.5 * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 4 * dpr;

      this.drawCatmullRomSpline(ctx, screenPoints, false, w, h);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1 * dpr;
      ctx.setLineDash([3 * dpr, 3 * dpr]);
      this.drawCatmullRomSpline(ctx, screenPoints, false, w, h);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  private renderActiveHourCell(
    canvas: HTMLCanvasElement,
    bucket: HourBucket | null | undefined,
    progressInHour: number,
    yMin: number,
    yMax: number,
    phase: number
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const dpr = window.devicePixelRatio || 1;

    ctx.clearRect(0, 0, w, h);
    if (!bucket || !bucket.points || bucket.points.length === 0) return;

    const points = bucket.points;
    const isSideways = this.isBucketSideways(bucket);
    const isBullish = !isSideways && bucket.close >= bucket.open;

    let strokeColor = '#94A3B8';
    let glowColor = 'rgba(148, 163, 184, 0.35)';
    if (!isSideways) {
      strokeColor = isBullish ? '#3CE88E' : '#FF2E56';
      glowColor = isBullish ? 'rgba(60, 232, 142, 0.35)' : 'rgba(255, 46, 86, 0.35)';
    }

    const activeWidth = Math.max(6 * dpr, progressInHour * w);
    const count = points.length;
    const screenPoints: { x: number; y: number }[] = [];

    for (let i = 0; i < count; i++) {
      const x = count > 1 ? (i / (count - 1)) * activeWidth : activeWidth;
      const clampedPrice = Math.max(yMin, Math.min(yMax, points[i]));
      const y = h - ((clampedPrice - yMin) / (yMax - yMin)) * (h - 8 * dpr) - 4 * dpr;
      screenPoints.push({ x, y });
    }

    if (screenPoints.length < 2) return;

    const fillGradient = ctx.createLinearGradient(0, 0, 0, h);
    if (isSideways) {
      fillGradient.addColorStop(0, 'rgba(148, 163, 184, 0.18)');
      fillGradient.addColorStop(0.7, 'rgba(148, 163, 184, 0.04)');
      fillGradient.addColorStop(1, 'rgba(148, 163, 184, 0.0)');
    } else if (isBullish) {
      fillGradient.addColorStop(0, 'rgba(60, 232, 142, 0.22)');
      fillGradient.addColorStop(0.7, 'rgba(60, 232, 142, 0.05)');
      fillGradient.addColorStop(1, 'rgba(60, 232, 142, 0.0)');
    } else {
      fillGradient.addColorStop(0, 'rgba(255, 46, 86, 0.22)');
      fillGradient.addColorStop(0.7, 'rgba(255, 46, 86, 0.05)');
      fillGradient.addColorStop(1, 'rgba(255, 46, 86, 0.0)');
    }

    ctx.fillStyle = fillGradient;
    this.drawCatmullRomSpline(ctx, screenPoints, true, w, h);
    ctx.fill();

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1.6 * dpr;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 6 * dpr;

    this.drawCatmullRomSpline(ctx, screenPoints, false, w, h);
    ctx.stroke();
    ctx.restore();

    const tip = screenPoints[screenPoints.length - 1];

    if (tip.x < w - 2 * dpr) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.lineWidth = 1 * dpr;
      ctx.setLineDash([2 * dpr, 3 * dpr]);
      ctx.beginPath();
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(w, tip.y);
      ctx.stroke();
      ctx.restore();
    }

    const pulseScale = 1 + 0.35 * Math.sin(phase);
    let dotPulseColor = 'rgba(148, 163, 184, 0.25)';
    if (!isSideways) {
      dotPulseColor = isBullish ? 'rgba(60, 232, 142, 0.25)' : 'rgba(255, 46, 86, 0.25)';
    }
    ctx.fillStyle = dotPulseColor;
    ctx.beginPath();
    ctx.arc(tip.x, tip.y, 5.5 * dpr * pulseScale, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = strokeColor;
    ctx.shadowBlur = 7 * dpr;
    ctx.beginPath();
    ctx.arc(tip.x, tip.y, 2.5 * dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  private drawCatmullRomSpline(
    ctx: CanvasRenderingContext2D,
    points: { x: number; y: number }[],
    isFill: boolean,
    w: number,
    h: number
  ): void {
    if (points.length < 2) return;

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
    }

    if (isFill) {
      const last = points[points.length - 1];
      ctx.lineTo(last.x, h);
      ctx.lineTo(points[0].x, h);
      ctx.closePath();
    }
  }

  private setupTooltipEvents(): void {
    if (!this.tooltipEl) return;

    this.gridContainer.addEventListener('mousemove', (e: MouseEvent) => {
      const cell = (e.target as HTMLElement).closest<HTMLElement>('.cell');
      if (!cell) {
        if (this.tooltipEl) this.tooltipEl.classList.remove('visible');
        return;
      }

      this.updateTooltipContent(cell);
      if (this.tooltipEl) {
        this.tooltipEl.classList.add('visible');
        const x = e.clientX + 14;
        const y = e.clientY + 14;
        const maxX = window.innerWidth - 230;
        const maxY = window.innerHeight - 150;
        this.tooltipEl.style.left = `${Math.min(x, maxX)}px`;
        this.tooltipEl.style.top = `${Math.min(y, maxY)}px`;
      }
    });

    this.gridContainer.addEventListener('mouseleave', () => {
      if (this.tooltipEl) this.tooltipEl.classList.remove('visible');
    });
  }

  private updateTooltipContent(cell: HTMLElement): void {
    if (!this.tooltipEl) return;
    const ticker = cell.dataset.ticker || '';
    const localHour = parseInt(cell.dataset.hour || '0', 10);
    const now = new Date();
    const currentLocalHour = now.getHours();

    const tMatrix = this.matrixData.get(ticker);
    const bucket = tMatrix && tMatrix.hours ? tMatrix.hours[localHour] : null;

    const nextLocalHour = (localHour + 1) % 24;
    const hStr = String(localHour).padStart(2, '0');
    const nextHStr = String(nextLocalHour).padStart(2, '0');

    let statusClass = 'past';
    let statusText = 'COMPLETED';
    if (localHour === currentLocalHour) {
      statusClass = 'current';
      statusText = 'LIVE ACTIVE';
    } else if (localHour > currentLocalHour) {
      statusClass = 'future';
      statusText = 'UPCOMING';
    }

    const fmt = (p: number | undefined | null) => {
      if (typeof p !== 'number') return '—';
      return p >= 1000
        ? p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : p >= 1.0
        ? p.toFixed(2)
        : p.toFixed(6);
    };

    if (!bucket) {
      this.tooltipEl.innerHTML = `
        <div class="tooltip-header">
          <span class="tooltip-ticker">${ticker}</span>
          <span class="tooltip-hour">${hStr}:00 - ${nextHStr}:00</span>
        </div>
        <span class="tooltip-status ${statusClass}">${statusText}</span>
      `;
      return;
    }

    const isSideways = this.isBucketSideways(bucket);
    const sign = bucket.change_pct >= 0 ? '+' : '';
    const changeColor = isSideways ? '#94A3B8' : (bucket.change_pct >= 0 ? 'var(--green)' : 'var(--red)');
    const arrow = isSideways ? '▶' : (bucket.change_pct >= 0 ? '▲' : '▼');

    this.tooltipEl.innerHTML = `
      <div class="tooltip-header">
        <span class="tooltip-ticker">${ticker}</span>
        <span class="tooltip-hour">${hStr}:00 - ${nextHStr}:00</span>
      </div>
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
        <span class="tooltip-status ${statusClass}">${statusText}</span>
        <span style="font-size:10px; color:#94A3B8;">${bucket.points ? bucket.points.length : 0} ticks</span>
      </div>
      <div class="tooltip-grid">
        <div class="tooltip-row">
          <span class="tooltip-lbl">Open:</span>
          <span class="tooltip-val">$${fmt(bucket.open)}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-lbl">Close:</span>
          <span class="tooltip-val">$${fmt(bucket.close)}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-lbl">High:</span>
          <span class="tooltip-val" style="color:var(--green);">$${fmt(bucket.high)}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-lbl">Low:</span>
          <span class="tooltip-val" style="color:var(--red);">$${fmt(bucket.low)}</span>
        </div>
        <div class="tooltip-change">
          <span class="tooltip-lbl">Hour Change:</span>
          <span style="color:${changeColor};">${arrow} ${sign}${bucket.change_pct.toFixed(2)}%</span>
        </div>
        ${isSideways ? '<div style="font-size:10px; color:#94A3B8; text-align:right; margin-top:2px;">SIDEWAYS / FLAT</div>' : ''}
      </div>
    `;
  }

  public calculateRankingScore(ticker: string): {
    score: number;
    greenCount: number;
    momentum: number;
    chg0: number;
    chg1: number;
    chg2: number;
    chg24: number;
  } {
    const tMatrix = this.matrixData.get(ticker);
    const liveInfo = this.livePrices.get(ticker);
    const now = new Date();
    const currentLocalHour = now.getHours();

    const h0 = currentLocalHour;
    const h1 = (h0 - 1 + 24) % 24;
    const h2 = (h0 - 2 + 24) % 24;

    const b0 = tMatrix && tMatrix.hours ? tMatrix.hours[h0] : null;
    const b1 = tMatrix && tMatrix.hours ? tMatrix.hours[h1] : null;
    const b2 = tMatrix && tMatrix.hours ? tMatrix.hours[h2] : null;

    const g0 = b0 ? (b0.close >= b0.open || (b0.change_pct ?? 0) >= 0 ? 1 : 0) : 0;
    const g1 = b1 ? (b1.close >= b1.open || (b1.change_pct ?? 0) >= 0 ? 1 : 0) : 0;
    const g2 = b2 ? (b2.close >= b2.open || (b2.change_pct ?? 0) >= 0 ? 1 : 0) : 0;
    const greenCount = g0 + g1 + g2;

    const chg0 = b0 ? (b0.change_pct ?? (b0.open > 0 ? ((b0.close - b0.open) / b0.open) * 100 : 0)) : 0;
    const chg1 = b1 ? (b1.change_pct ?? (b1.open > 0 ? ((b1.close - b1.open) / b1.open) * 100 : 0)) : 0;
    const chg2 = b2 ? (b2.change_pct ?? (b2.open > 0 ? ((b2.close - b2.open) / b2.open) * 100 : 0)) : 0;
    const chg24 = liveInfo ? liveInfo.changePct : (tMatrix?.change_pct_24h ?? 0);

    const momentum = (chg0 * 2.5) + (chg1 * 1.5) + (chg2 * 1.0) + (chg24 * 0.2);
    const score = (greenCount * 10000) + momentum;

    return { score, greenCount, momentum, chg0, chg1, chg2, chg24 };
  }

  public applyRealTimeRanking(): void {
    if (!this.rankingEnabled) return;

    let baseOrder = 0;
    for (const [sectionName, tickers] of this.sectionGroups.entries()) {
      const divider = this.sectionDividers.get(sectionName);
      if (divider) {
        divider.style.order = String(baseOrder);
      }
      baseOrder += 5;

      const scored = tickers.map(sym => {
        const stats = this.calculateRankingScore(sym);
        return { sym, ...stats };
      });

      // Sort according to active sort mode (growth, fall, market_cap, name)
      scored.sort((a, b) => {
        if (this.currentSortMode === 'growth') {
          // 1. По росту: наибольший суточный рост вверху
          if (b.chg24 !== a.chg24) {
            return b.chg24 - a.chg24;
          }
          if (b.greenCount !== a.greenCount) {
            return b.greenCount - a.greenCount;
          }
          return a.sym.localeCompare(b.sym);
        } else if (this.currentSortMode === 'fall') {
          // 2. По падению: наибольшее снижение вверху (наиболее отрицательные первыми)
          if (a.chg24 !== b.chg24) {
            return a.chg24 - b.chg24;
          }
          if (a.greenCount !== b.greenCount) {
            return a.greenCount - b.greenCount;
          }
          return a.sym.localeCompare(b.sym);
        } else if (this.currentSortMode === 'market_cap') {
          // 3. По капилизации: канонический порядок Top-100 CMC (BTC #1, ETH #2, SOL #3...)
          const rankA = this.canonicalCapIndex.get(a.sym) ?? 999;
          const rankB = this.canonicalCapIndex.get(b.sym) ?? 999;
          return rankA - rankB;
        } else {
          // 4. По имени: строгий алфавитный порядок (A-Z)
          return a.sym.localeCompare(b.sym);
        }
      });

      let green3Count = 0;

      scored.forEach((item, idx) => {
        const rank = idx + 1;
        const row = document.getElementById(`row-${item.sym}`);
        if (!row) return;

        row.style.order = String(baseOrder + idx);

        if (item.greenCount === 3) green3Count++;

        // Delta shift
        const prevRank = this.previousRanks.get(item.sym);
        const shiftEl = document.getElementById(`shift-${item.sym}`);
        if (shiftEl) {
          if (prevRank === undefined) {
            shiftEl.textContent = '—';
            shiftEl.className = 'rank-shift neutral';
          } else if (prevRank > rank) {
            shiftEl.textContent = `▲${prevRank - rank}`;
            shiftEl.className = 'rank-shift up';
          } else if (prevRank < rank) {
            shiftEl.textContent = `▼${rank - prevRank}`;
            shiftEl.className = 'rank-shift down';
          } else {
            shiftEl.textContent = '—';
            shiftEl.className = 'rank-shift neutral';
          }
        }
        this.previousRanks.set(item.sym, rank);

        // Rank badge
        const rankEl = document.getElementById(`rank-${item.sym}`);
        if (rankEl) {
          if (rank === 1) {
            rankEl.textContent = '#1';
            rankEl.className = 'asset-rank-badge rank-top1';
          } else if (rank === 2) {
            rankEl.textContent = '#2';
            rankEl.className = 'asset-rank-badge rank-top2';
          } else if (rank === 3) {
            rankEl.textContent = '#3';
            rankEl.className = 'asset-rank-badge rank-top3';
          } else if (rank <= 10) {
            rankEl.textContent = `#${rank}`;
            rankEl.className = 'asset-rank-badge rank-top10';
          } else {
            rankEl.textContent = `#${rank}`;
            rankEl.className = 'asset-rank-badge rank-other';
          }
        }

        // Squares momentum tag
        const sqTagEl = document.getElementById(`sqtag-${item.sym}`);
        if (sqTagEl) {
          if (item.greenCount === 3) {
            sqTagEl.textContent = '3/3';
            sqTagEl.className = 'squares-momentum-badge sq-3';
            sqTagEl.title = 'All 3 recent hours green (maximum upward momentum)';
          } else if (item.greenCount === 2) {
            sqTagEl.textContent = '2/3';
            sqTagEl.className = 'squares-momentum-badge sq-2';
            sqTagEl.title = '2 of 3 recent hours green';
          } else if (item.greenCount === 1) {
            sqTagEl.textContent = '1/3';
            sqTagEl.className = 'squares-momentum-badge sq-1';
            sqTagEl.title = '1 of 3 recent hours green (rebound)';
          } else {
            sqTagEl.textContent = '0/3';
            sqTagEl.className = 'squares-momentum-badge sq-0';
            sqTagEl.title = 'All recent hours red (decline)';
          }
        }

        // Visual row highlights
        row.classList.toggle('has-3-green', item.greenCount === 3);
        row.classList.toggle('rank-leader', rank === 1);
        row.classList.toggle('is-falling', item.greenCount === 0);
      });

      if (divider && sectionName === 'CRYPTO') {
        const badgeEl = divider.querySelector('.divider-ranking-badge');
        if (badgeEl) {
          const modeLabels: Record<SortMode, string> = {
            growth: `SORT: TOP GAINERS (${green3Count} WITH 3 GREEN SQUARES)`,
            fall: `SORT: TOP LOSERS`,
            market_cap: `SORT: MARKET CAP (TOP-100 CMC)`,
            name: `SORT: ALPHABETICAL (A-Z)`
          };
          badgeEl.textContent = modeLabels[this.currentSortMode] || '';
        }
      }

      baseOrder += tickers.length + 10;
    }
  }

  public getSortMode(): SortMode {
    return this.currentSortMode;
  }

  public setSortMode(mode: SortMode): void {
    this.currentSortMode = mode;
    try {
      localStorage.setItem('interlogs_trade_sort', mode);
    } catch (e) {}
    this.applyRealTimeRanking();
  }

  public setRankingEnabled(enabled: boolean): void {
    this.rankingEnabled = enabled;
    if (enabled) {
      this.applyRealTimeRanking();
    } else {
      // Reset order to default
      let idx = 0;
      for (const item of TICKER_CONFIG) {
        if (item.section) {
          const divider = this.sectionDividers.get(item.section);
          if (divider) divider.style.order = String(idx++);
        } else if (item.ticker) {
          const row = document.getElementById(`row-${item.ticker}`);
          if (row) row.style.order = String(idx++);
        }
      }
    }
  }

  public isRankingEnabled(): boolean {
    return this.rankingEnabled;
  }
}
